<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMenu } from '../composables/use-menu';
import { useCartStore } from '../stores/cart-store';
import { useNotifications } from '../composables/use-notifications';
import { type Category, Product } from '@shared/types/domain';
import CategoryTabs from '../components/menu/CategoryTabs.vue';
import ProductCard from '../components/menu/ProductCard.vue';
import ProductCardSkeleton from '../components/menu/ProductCardSkeleton.vue';
import ProductDetailModal from '../components/menu/ProductDetailModal.vue';
import Alert from '@shared/components/Alert.vue';
import EmptyState from '@shared/components/EmptyState.vue';
import { createLogger } from '@shared/utils/logger';

const log = createLogger('MenuPage');
const { t } = useI18n();

const {
  loading,
  error,
  searchTerm,
  selectedCategoryId,
  filteredProducts,
  visibleProducts,
  availableCategories,
  homeModules,
  fetchMenu,
  loadMore,
  // V3.4 paginated
  paginatedItems,
  paginatedHasMore,
  paginatedLoading,
  loadNextPage,
} = useMenu();

// V3.4: use paginated items when category selected, fallback to filtered, always filtered by searchTerm
const displayProducts = computed(() => {
  const baseProducts = selectedCategoryId.value && paginatedItems.value.length > 0
    ? paginatedItems.value
    : visibleProducts.value;

  const term = String(searchTerm.value || '').trim().toLowerCase();
  if (!term) return baseProducts;
  return (baseProducts as any[]).filter((p: any) =>
    String(p.name || '').toLowerCase().includes(term) ||
    String(p.description || '').toLowerCase().includes(term)
  );
});

const emptyResultsTitle = computed(() => {
  const term = String(searchTerm.value || '').trim();
  if (term) {
    return t('clients.menu.empty_title_for_term', { term });
  }
  return t('clients.menu.empty_title');
});

const emptyResultsDescription = computed(() => {
  const term = String(searchTerm.value || '').trim();
  if (term) {
    return t('clients.menu.empty_subtitle_for_term', { term });
  }
  return t('clients.menu.empty_subtitle');
});

const selectedProduct = ref<Product | null>(null);
const isProductModalOpen = ref(false);
const editingCartIndex = ref<number | null>(null);
const editingCartItem = ref<any>(null);

const cartStore = useCartStore();
const notifications = useNotifications();

const handleProductSelect = (product: Product) => {
  editingCartIndex.value = null;
  editingCartItem.value = null;
  selectedProduct.value = product;
  isProductModalOpen.value = true;
  log.debug('Product selected', product.name);
};

// #980 (C4): quick-add path — only reachable for products with no required
// modifier groups / package components (ProductCard guards that). Adds one
// unit with no modifiers straight to the cart, mirroring the modal payload.
const handleQuickAdd = (product: Product) => {
  cartStore.addItem({
    id: product.id,
    menu_item_id: product.id,
    name: product.name,
    price: product.price,
    quantity: 1,
    extras: [],
    extrasTotal: 0,
    modifiers: [],
    modifier_none_groups: [],
    modifier_groups: (product.modifier_groups || []).map((group) => ({
      ...group,
      modifiers: group.modifiers || [],
    })),
    package_components: (product.package_components || []).map((component) => ({
      component_item_id: component.component_item_id,
      menu_item_id: component.menu_item_id || component.component_item_id,
      name: component.name,
      quantity: component.quantity,
      modifiers: [],
      modifier_none_groups: [],
    })),
    special_instructions: '',
  });
  notifications.success(`${product.name} agregado al carrito`);
  log.debug('Product quick-added', product.name);
};

const openEditModal = (item: any, index: number) => {
  editingCartIndex.value = index;
  editingCartItem.value = item;
  const productId = String(item.menu_item_id || item.id);
  const found = (filteredProducts.value as Product[]).find(p => String(p.id) === productId);
  selectedProduct.value = found || ({
    id: productId,
    name: item.name,
    price: item.price,
    description: item.description || '',
    modifier_groups: item.modifier_groups || [],
    package_components: item.package_components || [],
  } as unknown as Product);
  isProductModalOpen.value = true;
};

defineExpose({ openEditModal });

const normalizedHomeModules = computed(() =>
  (homeModules.value || []).filter((module) => Array.isArray(module.items) && module.items.length > 0)
);

const categorizedSections = computed(() => {
  if (selectedCategoryId.value !== null || searchTerm.value) {
    return [] as Array<{ category: Category; products: Product[]; hasMore: boolean; remainingCount: number; totalCount: number }>;
  }

  const grouped = new Map<string, Product[]>();
  for (const product of filteredProducts.value as Product[]) {
    const productCategoryId = String((product as Product & { categoryRepresentativeId?: string }).categoryRepresentativeId ?? product.category_id ?? '');
    if (!productCategoryId) continue;
    const categoryProducts = grouped.get(productCategoryId);
    if (categoryProducts) {
      categoryProducts.push(product);
      continue;
    }
    grouped.set(productCategoryId, [product]);
  }

  return (availableCategories.value as Array<{ id: string; name: string }>).map((category) => {
    const allProducts = grouped.get(String(category.id)) || [];
    return {
      category: { id: category.id, name: category.name } as Category,
      products: allProducts.slice(0, 24),
      hasMore: allProducts.length > 24,
      remainingCount: allProducts.length - 24,
      totalCount: allProducts.length,
    };
  }).filter((section) => section.products.length > 0);
});

const closeProductModal = () => {
  isProductModalOpen.value = false;
};

// ── V3.3 Infinite Scroll ─────────────────────────────────────
const sentinel = ref<HTMLElement | null>(null);
let observer: IntersectionObserver | null = null;

const setupInfiniteScroll = () => {
  if (observer) { observer.disconnect(); observer = null; }
  if (!sentinel.value) return;
  observer = new IntersectionObserver(
    (entries) => {
      if (entries[0]?.isIntersecting && paginatedHasMore.value && !paginatedLoading.value) {
        loadNextPage();
      }
    },
    { rootMargin: '200px' }  // trigger 200px before sentinel is visible
  );
  observer.observe(sentinel.value);
};

watch(sentinel, (el) => { if (el) setupInfiniteScroll(); });
watch(selectedCategoryId, () => { if (sentinel.value) setupInfiniteScroll(); });
// Clear category selection when search is cleared
watch(searchTerm, (newVal) => {
  if (!newVal && selectedCategoryId.value !== null) {
    selectedCategoryId.value = null;
  }
});
onUnmounted(() => { observer?.disconnect(); });

onMounted(async () => {
  fetchMenu();
});
</script>

<template>
  <div class="menu-page">
    <!-- Header with Search -->
    <header class="menu-page__header">
      <div class="search-container">
        <div class="menu-search">
          <svg class="menu-search__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="M21 21l-4.35-4.35"></path>
          </svg>
          <input
            v-model="searchTerm"
            type="search"
            class="menu-search__input"
            :placeholder="t('clients.menu.search_placeholder')"
            :disabled="loading"
            autocomplete="off"
            aria-label="Buscar productos en el menú"
          />
        </div>
      </div>
    </header>

    <CategoryTabs
      v-if="!loading && !error"
      v-model="selectedCategoryId"
      :categories="availableCategories"
    />

    <!-- Main Content -->
    <main class="menu-page__content">
      <!-- Loading State with Skeletons -->
      <div v-if="loading" class="skeleton-grid" role="status" aria-live="polite" aria-label="Cargando menú">
        <div v-for="i in 8" :key="i" class="grid-item">
          <ProductCardSkeleton />
        </div>
      </div>

      <!-- Error State -->
      <Alert v-else-if="error" type="error" @close="fetchMenu" role="alert" aria-live="assertive">
        {{ error }}
        <template #actions>
          <button class="btn-retry" @click="fetchMenu">{{ t('clients.menu.retry') }}</button>
        </template>
      </Alert>

      <section
        v-if="!loading && !error && selectedCategoryId === null && !searchTerm && normalizedHomeModules.length"
        class="home-modules"
      >
        <div
          v-for="module in normalizedHomeModules"
          :key="module.id || module.slug || module.title"
          class="home-module"
        >
          <div class="category-header">
            <h2 class="category-title">{{ module.title || 'Destacados' }}</h2>
            <div class="category-divider"></div>
          </div>
          <div class="products-grid">
            <div v-for="product in module.items" :key="`home-${module.slug}-${product.id}`" class="grid-item">
              <ProductCard :product="product" @select="handleProductSelect" @quick-add="handleQuickAdd" />
            </div>
          </div>
        </div>
      </section>

      <!-- Empty Results -->
      <EmptyState
        v-if="!loading && !error && filteredProducts.length === 0"
        icon="search"
        :title="emptyResultsTitle"
        :description="emptyResultsDescription"
        role="status"
        aria-live="polite"
      >
        <template #actions>
          <button class="btn-clear" @click="searchTerm = ''; selectedCategoryId = null">
            {{ t('clients.menu.view_all') }}
          </button>
        </template>
      </EmptyState>

      <!-- Products Grid (Categorized or Filtered) -->
      <div v-if="!loading && !error && filteredProducts.length > 0" class="menu-sections">
        <template v-if="selectedCategoryId === null && !searchTerm">
          <!-- Categorized View (All) -->
          <section
            v-for="section in categorizedSections"
            :key="section.category.id"
            class="menu-category-section"
          >
            <div class="category-header">
              <h2 class="category-title">{{ section.category.name }}</h2>
              <div class="category-divider"></div>
            </div>

            <div class="products-grid">
              <div
                v-for="product in section.products"
                :key="product.id"
                class="grid-item"
              >
                <ProductCard
                  :product="product"
                  @select="handleProductSelect"
                  @quick-add="handleQuickAdd"
                />
              </div>
            </div>

            <div v-if="section.hasMore" class="category-section-more">
              <button 
                class="btn-view-category" 
                @click="selectedCategoryId = String(section.category.id)"
              >
                Ver todos ({{ section.remainingCount }} más)
              </button>
            </div>
          </section>
        </template>
        
        <template v-else>
          <!-- Flat View: V3.4 paginated (category) or V3.2 slice (search/filter) -->
          <div class="products-grid">
            <TransitionGroup name="product-list">
              <div
                v-for="product in displayProducts"
                :key="product.id"
                class="grid-item"
              >
                <ProductCard
                  :product="product"
                  @select="handleProductSelect"
                  @quick-add="handleQuickAdd"
                />
              </div>
            </TransitionGroup>
          </div>

          <!-- V3.3 + V3.4: infinite scroll sentinel (paginated categories) -->
          <div
            v-if="selectedCategoryId"
            ref="sentinel"
            class="scroll-sentinel"
            aria-hidden="true"
          >
            <div v-if="paginatedLoading" class="scroll-sentinel__spinner">
              <div class="spinner-ring"></div>
            </div>
          </div>

          <!-- V3.2: client-side load more (search/filter fallback) -->
          <div v-if="!selectedCategoryId && filteredProducts.length > visibleProducts.length" class="load-more-container">
            <button class="btn-load-more" @click="loadMore">
              Mostrar más ({{ filteredProducts.length - visibleProducts.length }} restantes)
            </button>
          </div>
        </template>
      </div>
    </main>

    <ProductDetailModal
      v-model="isProductModalOpen"
      :product="selectedProduct"
      :edit-index="editingCartIndex"
      :initial-item="editingCartItem"
      @added="closeProductModal"
      @updated="closeProductModal"
    />
  </div>
</template>

<style scoped>
.menu-page {
  --primary: #c2410c;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: linear-gradient(135deg, #fcfcfd 0%, #f1f5f9 100%);
  font-family: var(--enfa-font-family);
}

.menu-page__header {
  padding: 1.5rem 1rem 1rem;
  background: transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.search-container {
  width: 100%;
  max-width: 680px;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s ease;
  border-radius: 16px;
}

.menu-search {
  position: relative;
}

.menu-search__icon {
  position: absolute;
  left: 0.9rem;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  pointer-events: none;
}

.menu-search__input {
  width: 100%;
  padding: 0.85rem 1rem 0.85rem 2.65rem;
  border: 1px solid #dbe2ea;
  border-radius: 14px;
  font-size: 0.95rem;
  color: #0f172a;
  background: #ffffff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.menu-search__input::placeholder {
  color: #94a3b8;
}

.menu-search__input:focus-visible {
  outline: none;
  border-color: #e05a2a;
  box-shadow: 0 0 0 3px rgba(255, 107, 53, 0.15);
}

.menu-search__input:disabled {
  background: #f8fafc;
  color: #64748b;
}

.search-container:focus-within {
  transform: translateY(-2px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.08);
}

.menu-page__content {
  flex: 1;
  padding: 2rem 1.5rem;
}

.home-modules {
  display: flex;
  flex-direction: column;
  gap: 1.8rem;
  margin-bottom: 1.8rem;
}

/* Skeleton Grid */
.skeleton-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 1.5rem;
  max-width: 1280px;
  margin: 0 auto;
}

@media (min-width: 640px) {
  .skeleton-grid {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }
}

@media (min-width: 768px) {
  .skeleton-grid {
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 2rem;
  }
}

@media (min-width: 1024px) {
  .skeleton-grid {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  }
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 5rem 0;
  gap: 1.25rem;
  color: var(--muted, #64748b);
  font-weight: 500;
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.empty-results {
  text-align: center;
  padding: 6rem 2rem;
  color: var(--muted, #64748b);
  animation: fadeIn 0.4s ease-out;
}

.empty-icon {
  font-size: 4.5rem;
  margin-bottom: 1.5rem;
  filter: drop-shadow(0 4px 6px rgba(0,0,0,0.1));
}

.empty-results h3 {
  font-size: 1.5rem;
  color: var(--text, #1e293b);
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.menu-category-section {
  margin-bottom: 3.5rem;
  animation: fadeIn 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.offers-section {
  background: linear-gradient(135deg, rgba(255, 107, 53, 0.05) 0%, rgba(255, 107, 53, 0.02) 100%);
  border-radius: 24px;
  padding: 1.5rem;
  margin-bottom: 3.5rem;
  border: 1px solid rgba(255, 107, 53, 0.1);
  box-shadow: 0 4px 20px rgba(255, 107, 53, 0.05);
}

.offers-title {
  color: var(--primary, #e05a2a);
}

.offers-divider {
  background: linear-gradient(90deg, rgba(255, 107, 53, 0.3) 0%, rgba(255, 107, 53, 0) 100%);
}

.category-header {
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  gap: 1rem;
}

.category-title {
  font-size: 1.5rem;
  font-weight: 800;
  color: var(--text, #1e293b);
  margin: 0;
  white-space: nowrap;
}

.category-divider {
  flex: 1;
  height: 2px;
  background: linear-gradient(90deg, #e2e8f0 0%, rgba(226, 232, 240, 0) 100%);
  border-radius: 2px;
}

.products-grid {
  position: relative;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 1.5rem;
  max-width: 1280px;
  margin: 0 auto;
}

@media (min-width: 640px) {
  .products-grid {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }
}

@media (min-width: 768px) {
  .products-grid {
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 2rem;
  }
}

@media (min-width: 1024px) {
  .products-grid {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  }
}

.btn-retry, .btn-clear {
  margin-top: 1.5rem;
  padding: 0.75rem 2rem;
  border-radius: 999px;
  background: var(--primary, #e05a2a);
  color: white;
  border: none;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(255, 107, 53, 0.3);
}

.btn-retry:hover, .btn-clear:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(255, 107, 53, 0.4);
  background: #ff5216; /* slightly darker/more intense */
}

.btn-retry:active, .btn-clear:active {
  transform: translateY(0);
}

/* Animations */
.product-list-move,
.product-list-enter-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.product-list-leave-active {
  transition: opacity 0.12s ease;
  position: absolute;
  pointer-events: none;
}
.product-list-enter-from,
.product-list-leave-to {
  opacity: 0;
  transform: scale(0.98);
}

@media (prefers-reduced-motion: reduce) {
  .product-list-move,
  .product-list-enter-active,
  .product-list-leave-active {
    transition: none !important;
  }
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.grid-item {
  display: flex;
}

/* V3.3 Infinite scroll sentinel */
.scroll-sentinel {
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1rem;
}
.scroll-sentinel__spinner {
  display: flex;
  align-items: center;
  justify-content: center;
}
.spinner-ring {
  width: 28px;
  height: 28px;
  border: 3px solid var(--color-gray-200, #e2e8f0);
  border-top-color: var(--color-primary, #e05a2a);
  border-radius: 50%;
  animation: spin-ring 0.8s linear infinite;
}
@keyframes spin-ring { to { transform: rotate(360deg); } }

.load-more-container {
  display: flex;
  justify-content: center;
  margin-top: 2.5rem;
  width: 100%;
}

.btn-load-more {
  padding: 0.85rem 2.2rem;
  border-radius: 999px;
  background: var(--primary, #c2410c);
  color: white;
  border: none;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 14px rgba(194, 65, 12, 0.35);
}

.btn-load-more:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(194, 65, 12, 0.45);
  background: #a13207;
}

.btn-load-more:active {
  transform: translateY(0);
}

.category-section-more {
  display: flex;
  justify-content: center;
  margin-top: 1.5rem;
}

.btn-view-category {
  padding: 0.6rem 1.6rem;
  border-radius: 999px;
  background: transparent;
  color: var(--primary, #c2410c);
  border: 2px solid var(--primary, #c2410c);
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-view-category:hover {
  background: var(--primary, #c2410c);
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(194, 65, 12, 0.25);
}

.btn-view-category:active {
  transform: translateY(0);
}
</style>
